import type { ApolloClient } from '@apollo/client';

import type {
  TaskFieldsFragment,
  TaskActionSource,
} from '../../../__generated__/graphql';
import { engagementTasksFragment } from '../../../features/tasks';
import type { PubNubTask } from '../../../types/pubnubTypes';

// Helper function to convert PubNubTask to TaskFieldsFragment
export const createTaskDataFromPubNubTask = (
  task: PubNubTask,
): TaskFieldsFragment => {
  return {
    __typename: 'TaskQL',
    id: task.id,
    title: task.title,
    status: task.status,
    engagementId: task.engagement_id,
    completedAt: task.completed_at || null,
    completedByParticipant: task.completed_by_participant
      ? {
          __typename: 'ParticipantQL',
          displayName: task.completed_by_participant.display_name,
        }
      : null,
    completionSource: (task.completion_source as TaskActionSource) || null,
    dismissedAt: task.dismissed_at || null,
    dismissedByParticipant: task.dismissed_by_participant
      ? {
          __typename: 'ParticipantQL',
          displayName: task.dismissed_by_participant.display_name,
        }
      : null,
    dismissalSource: (task.dismissal_source as TaskActionSource) || null,
  };
};

// Helper function to upsert a task for an engagement in the cache
export const upsertTaskForEngagement = (
  client: ApolloClient<any>,
  task: PubNubTask,
) => {
  const engagementId = task.engagement_id;
  const taskData: TaskFieldsFragment = createTaskDataFromPubNubTask(task);

  client.cache.updateFragment(
    {
      id: `EngagementQL:${engagementId}`,
      fragment: engagementTasksFragment,
      fragmentName: 'EngagementTasks',
    },
    engagementData => {
      if (!engagementData) return undefined;

      const existingTasks = engagementData.tasks || [];

      const updatedTasks = [...existingTasks];
      const existingTaskIndex = updatedTasks.findIndex(
        existingTask => existingTask.id === taskData.id,
      );

      if (existingTaskIndex !== -1) {
        updatedTasks[existingTaskIndex] = taskData;
      } else {
        updatedTasks.push(taskData);
      }

      return {
        ...engagementData,
        tasks: updatedTasks,
      };
    },
  );
};
